// External libraries
import Grid from '@mui/material/Grid2';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// API
import { useCreateSalutationMutation, useUpdateSalutationMutation } from '@api/salutations/salutations-api';

// Models
import { SalutationApiModel } from '@models/ApiModels/salutation-api-model';
import { Salutation } from '@models/DomainModels/Master/salutation';

// Common components
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';
import { useEntityDetail, EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

// State management
import { DynamicStore } from '@state/interfaces/idynamic-store';

// Form Validation
import { FormFactory } from '@services/validation/form-factory';
import { useDecoratorForm } from '@hooks/useDecoratorForm';

// Permissions
import { useFetchToken } from '@hooks/useFetchToken';

// Props interface for the SalutationDetailDialog component
type SalutationDetailDialogProps = {
  salutationData?: Salutation;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<Salutation>) => void;
  store: DynamicStore | undefined;
  token: string;
  readPermission?: boolean;
  writePermission?: boolean;
};

function SalutationDetailDialog({
  salutationData,
  organizationId,
  open,
  onClose,
  token,
  readPermission = false,
  writePermission = false
}: Readonly<SalutationDetailDialogProps>) {
  const { t } = useTranslation(['salutations', 'common']);

  // RTK Query hooks
  const [createSalutation] = useCreateSalutationMutation();
  const [updateSalutation] = useUpdateSalutationMutation();

  // Initialize form using FormFactory
  const formConfig = FormFactory.getFormConfig(
    SalutationApiModel,
    salutationData ?? {
      name: ''
    },
    { showSnackbarOnError: true }
  );

  const {
    data: salutation,
    errors: validationErrors,
    isValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(formConfig.modelClass, formConfig.initialData, formConfig.options);

  // Memoize the isDirty value to prevent excessive re-renders
  const isDirtyValue = useMemo(() => isDirty(), [isDirty]);

  // Create an enhanced wrapper for validateForm with better logging and error handling
  const validateFormWrapper = useCallback(async (): Promise<boolean> => {
    // Run the decorator-based validation
    return (await validateForm()).isValid;
  }, [validateForm]);

  // Create entity function
  const createEntity = useCallback(async () => {
    const createdId = await createSalutation({
      organizationId,
      salutation: { name: salutation.name ?? '' },
      token
    }).unwrap();

    // Return the created entity with the ID from the server
    return { ...salutation, id: createdId } as Salutation;
  }, [createSalutation, organizationId, token, salutation]);

  // Update entity function
  const updateEntity = useCallback(async () => {
    if (!salutationData?.id) {
      throw new Error('updateEntity called without salutationData.id');
    }
    await updateSalutation({
      organizationId,
      salutationId: salutationData.id,
      salutation: { name: salutation.name ?? '' },
      token
    }).unwrap();

    // Return the updated entity with existing ID
    return { ...salutation, id: salutationData.id } as Salutation;
  }, [updateSalutation, organizationId, salutationData?.id, token, salutation]);

  // Create a wrapped onClose function that ensures the form is reset
  const handleDialogClose = useCallback(
    (result: EntityDialogCloseResult<Salutation>) => {
      resetForm();
      setFormChanged(false);
      onClose(result);
    },
    [onClose, resetForm, setFormChanged]
  );

  // Use the entity detail hook
  const { handleSave, loadingState } = useEntityDetail<Salutation>({
    entityName: t('entityName'),
    initialData: (salutationData ?? { name: '' }) as Salutation,
    open,
    onClose: handleDialogClose,
    createEntity,
    updateEntity,
    validateForm: validateFormWrapper,
    setData: data => setData(data as Partial<SalutationApiModel>, true),
    resetForm,
    loggerContext: 'SalutationDetailDialog',
    readPermission,
    writePermission,
    setFormChanged
  });

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: string, value: string | number) => {
      updateField(field, value);

      // Ensure we validate the field after updating
      setTimeout(() => validateField(field, value), 0);
    },
    [updateField, validateField]
  );

  // Handle field blur (validation)
  const handleFieldBlur = useCallback(
    (field: string) => {
      validateField(field, salutation[field as keyof SalutationApiModel]);
    },
    [validateField, salutation]
  );

  // Create form content
  const formContent = (
    <Grid container size={12}>
      <Grid size={12}>
        <CdfFormField
          name="name"
          label={t('detailDialog.name')}
          value={salutation.name}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="salutation-name-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.name'),
              readOnly: !writePermission
            }
          }}
        />
      </Grid>
    </Grid>
  );

  // Get save button text based on edit vs. create mode
  const saveButtonText = salutationData?.id ? t('saveChanges') : t('addButtonText');

  // Get cancel button text based on write permission
  const cancelButtonText = writePermission ? t('common:cancel') : t('common:close');

  // Get edit mode display title based on permissions
  const editModeTitle = writePermission ? t('edit') : t('view');

  // Get title text based on whether we're editing or creating
  const titleText = salutationData?.id ? editModeTitle : t('add');

  return (
    <EntityDetailDialog
      open={open}
      onClose={() => handleDialogClose({ action: 'cancelled' })}
      title={titleText}
      formContent={formContent}
      saveButtonText={saveButtonText}
      cancelButtonText={cancelButtonText}
      readPermission={readPermission}
      writePermission={writePermission}
      isLoading={false}
      loadingState={loadingState}
      loadingMessage={t('detailDialog.loadingMessage')}
      savingMessage={t('detailDialog.savingMessage')}
      isValid={isValid}
      isDirty={isDirtyValue}
      onSave={handleSave}
      dataTestId="salutation-detail-dialog"
      maxWidth="xs"
    />
  );
}

// Props interface for the SalutationDetailDialogWrapper component
type SalutationDetailDialogWrapperProps = {
  salutationData?: Salutation;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<Salutation>) => void;
  store: DynamicStore | undefined;
};

export function SalutationDetailDialogWrapper({
  salutationData,
  organizationId,
  open,
  onClose,
  store
}: Readonly<SalutationDetailDialogWrapperProps>) {
  const token = useFetchToken();

  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      entityType="Salutations"
      renderContent={permissions => (
        <SalutationDetailDialog
          salutationData={salutationData}
          organizationId={organizationId}
          open={open}
          onClose={onClose}
          store={store}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
        />
      )}
    />
  );
}
